import { useCallback, useEffect, useRef, useState } from 'react';
import { type AckEntry, type AckState, type HealthAlert, isAlertHidden } from './health-status.js';

const STORAGE_KEY = 'comms.alertAck.v1';
/** Snooze window — re-raises after an hour. */
export const SNOOZE_MS = 60 * 60 * 1000;
/** How long a resolved-✓ confirmation lingers before it clears. */
const RESOLVED_MS = 4000;

function loadAck(): AckState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    // R9 review #5 — shape-validate + drop expired snoozes so corrupt or stale
    // dismissal state can't wrongly suppress a live alert across a refresh.
    const now = Date.now();
    const clean: AckState = {};
    for (const [id, entry] of Object.entries(parsed as Record<string, unknown>)) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as AckEntry;
      const next: AckEntry = {};
      if (typeof e.ackSignature === 'string') next.ackSignature = e.ackSignature;
      if (typeof e.snoozeUntil === 'number' && e.snoozeUntil > now)
        next.snoozeUntil = e.snoozeUntil;
      if (next.ackSignature !== undefined || next.snoozeUntil !== undefined) clean[id] = next;
    }
    return clean;
  } catch {
    return {};
  }
}

function saveAck(state: AckState): void {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* best effort */
  }
}

export interface ResolvedAlert {
  id: string;
  title: string;
}

export interface AlertDock {
  /** Alerts to render now (active, minus acked/snoozed). */
  visible: HealthAlert[];
  /** Transient "✓ resolved" confirmations for conditions that just cleared. */
  resolved: ResolvedAlert[];
  acknowledge: (id: string) => void;
  snooze: (id: string) => void;
}

/**
 * R9 — the alert-dock state machine over the pure `deriveAlerts` output.
 *   - ACK hides an alert until its signature changes (a worse/different
 *     condition re-raises it).
 *   - SNOOZE hides it for an hour, then it re-raises.
 *   - A condition that fully CLEARS is pruned, so it re-raises if it returns,
 *     AND fires a brief resolved-✓ confirmation (the choreography).
 * Dismissal state is persisted (localStorage) so a refresh doesn't resurrect
 * everything the operator already dealt with.
 */
export function useAlertDock(alerts: HealthAlert[]): AlertDock {
  const [ack, setAck] = useState<AckState>(() => loadAck());
  const [resolved, setResolved] = useState<ResolvedAlert[]>([]);
  const titlesRef = useRef<Map<string, string>>(new Map());
  const prevActiveRef = useRef<Set<string>>(new Set());

  // Remember the latest title per id so a now-absent (resolved) alert can still
  // be named in its ✓ confirmation.
  for (const a of alerts) titlesRef.current.set(a.id, a.title);

  const activeKey = alerts
    .map((a) => a.id)
    .sort()
    .join('|');

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeKey encodes the active id set; alerts changes identity every poll.
  useEffect(() => {
    const active = new Set(alerts.map((a) => a.id));

    // (1) Prune ack state for conditions that fully cleared → they re-raise on return.
    setAck((prev) => {
      let changed = false;
      const next: AckState = {};
      for (const [id, entry] of Object.entries(prev)) {
        if (active.has(id)) next[id] = entry;
        else changed = true;
      }
      if (!changed) return prev;
      saveAck(next);
      return next;
    });

    // (2) Resolved-✓ choreography: anything active last tick but gone now.
    const gone = [...prevActiveRef.current].filter((id) => !active.has(id));
    const timers: number[] = [];
    if (gone.length > 0) {
      const items = gone.map((id) => ({ id, title: titlesRef.current.get(id) ?? 'Condition' }));
      setResolved((r) => [...r.filter((x) => !gone.includes(x.id)), ...items]);
      for (const id of gone) {
        timers.push(
          window.setTimeout(() => setResolved((r) => r.filter((x) => x.id !== id)), RESOLVED_MS),
        );
      }
    }
    prevActiveRef.current = active;
    // R9 review #3 — clear the expiry timers on teardown so they never fire
    // after unmount (operator leaves the view <4s) or stack up on rapid
    // clear→return→clear within the window.
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [activeKey]);

  const acknowledge = useCallback(
    (id: string) => {
      const sig = alerts.find((a) => a.id === id)?.signature;
      setAck((prev) => {
        const next: AckState = { ...prev, [id]: { ackSignature: sig } };
        saveAck(next);
        return next;
      });
    },
    [alerts],
  );

  const snooze = useCallback((id: string) => {
    setAck((prev) => {
      const next: AckState = { ...prev, [id]: { snoozeUntil: Date.now() + SNOOZE_MS } };
      saveAck(next);
      return next;
    });
  }, []);

  // R9 review #4 — `now` is read at render, so a snooze re-raises on the next
  // re-render after it expires, not to the second. The health queries poll
  // (60–120s) and the SSE store ticks, so the slack is bounded to ~1 poll —
  // acceptable for an hour-long snooze; no dedicated expiry timer.
  const now = Date.now();
  const visible = alerts.filter((a) => !isAlertHidden(a, ack[a.id], now));

  return { visible, resolved, acknowledge, snooze };
}
